import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';

const STORAGE_KEY = 'jq-cookie-consent';

// Public helpers consumed by other parts of the app (e.g. useAnalytics).
export const getCookieConsent = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => getCookieConsent() === 'accepted';

export const CookieConsent = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setTimeout(() => setShow(true), 1500);
    }
  }, []);

  const persist = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* ignore quota errors */
    }
    setShow(false);
  };

  const handleAccept = () => persist('accepted');
  const handleEssential = () => persist('essential');
  const handleReject = () => persist('rejected');

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          data-testid="cookie-consent"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="bg-card border border-border rounded-lg p-6 shadow-2xl backdrop-blur-xl relative">
              <button
                onClick={handleReject}
                className="absolute top-4 right-4 p-1 hover:bg-muted rounded-full transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium mb-1">Utilizamos cookies</h3>
                    <p className="text-sm text-muted-foreground">
                      Esta web utiliza cookies propias y de terceros para mejorar tu experiencia,
                      analizar el tráfico y personalizar el contenido. Puedes aceptar todas, usar
                      solo las imprescindibles o rechazarlas.{' '}
                      <Link
                        to="/politica-cookies"
                        onClick={() => setShow(false)}
                        className="underline hover:text-foreground transition-colors"
                        data-testid="cookie-policy-link"
                      >
                        Más información
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 w-full md:w-auto">
                  <Button
                    variant="ghost"
                    onClick={handleReject}
                    className="text-xs uppercase tracking-widest"
                    data-testid="cookie-reject"
                  >
                    Rechazar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleEssential}
                    className="text-xs uppercase tracking-widest"
                    data-testid="cookie-essential"
                  >
                    Solo imprescindibles
                  </Button>
                  <Button
                    onClick={handleAccept}
                    className="text-xs uppercase tracking-widest bg-foreground text-background hover:bg-foreground/90"
                    data-testid="cookie-accept"
                  >
                    Aceptar todas
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
